<script setup>
/**
 * A member ↔ restaurant thread as the restaurant's team reads it — the
 * Messages page and the Fans page's "Send message to …" (Kapil, 2026-09-25:
 * "should show the message history") draw it the same way from here.
 *
 * A member's highlight RM passed along arrives as a message from them, with a
 * line above it saying what it is: "Highlight shared by member who is a Fan
 * of NAIA" (utils/restaurantHighlights, tested).
 */
import { highlightMessageLabel, isHighlightMessage } from '@/utils/restaurantHighlights'

const props = defineProps({
  messages: { type: Array, default: () => [] },
  restaurantName: { type: String, default: '' },
  memberIsFan: { type: Boolean, default: false },
})

const label = () => highlightMessageLabel({ restaurantName: props.restaurantName, memberIsFan: props.memberIsFan })
</script>

<template>
  <div class="ra-message-list">
    <div
      v-for="msg in messages"
      :key="msg._id"
      class="ra-message-list__item"
    >
      <p
        v-if="isHighlightMessage(msg)"
        class="ra-message-list__label"
      >
        {{ label() }}
      </p>
      <div
        class="d-flex"
        :class="msg.senderType === 'restaurant' ? 'justify-end' : 'justify-start'"
      >
        <div
          class="ra-message-list__bubble rounded"
          :class="msg.senderType === 'restaurant' ? 'ra-message-list__bubble--own' : 'ra-message-list__bubble--other'"
        >
          <div
            v-if="msg.deletedAt"
            class="text-caption font-italic text-medium-emphasis"
          >
            [Message deleted]
          </div>
          <div
            v-else
            class="ra-message-list__text"
          >
            {{ msg.text }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ra-message-list__item + .ra-message-list__item {
  margin-top: 12px;
}

/* What the message is, above it — secondary to the member's words. */
.ra-message-list__label {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 600;
  color: var(--ds-text-secondary, rgba(var(--v-theme-on-surface), 0.6));
}

.ra-message-list__bubble {
  max-width: 70%;
  padding: 8px 12px;
}

.ra-message-list__bubble--own {
  background: var(--ds-brand-primary-light, #e9f4f3);
}

.ra-message-list__bubble--other {
  background: var(--ds-surface-alt, #f8f8f9);
  border: 1px solid var(--ds-border-default);
}

/* Their line breaks are part of what they wrote. */
.ra-message-list__text {
  font-size: 14px;
  line-height: 1.45;
  white-space: pre-line;
  overflow-wrap: anywhere;
}

@media (max-width: 600px) {
  .ra-message-list__bubble {
    max-width: 85%;
  }
}
</style>
